import { useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  SettingsType,
  applyPipeexSettingsToConfigurationStore,
  getActivePipeexProfile,
  getPipeexSettings,
  snapshotCurrentPcfSettingsStoreForDebug,
  summarizePcfSettingsDtoDataFlowForDebug,
  summarizePcfSettingsDtoForDebug,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { DEFAULT_PIPEEX_PROFILE_NAME } from '../../../shared/config/defaultPipeexProfile/defaultPipeexProfile';
import type { MappingConfiguration } from '../../../shared/types/config/mapping.types';
import { clearResumeIntoMappingIntent } from '../../../shared/lib/workflowResumeIntent';
import { buildProfileKey } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import {
  debugMappingProfile,
  elapsedMs,
  errorMessage,
  nowMs,
  startProfileStallWarning,
} from './profileControllerShared';

interface RestoreProfileRef {
  name: string;
  settingsType: SettingsType;
  profileKey: string;
}

interface UseProfileRestoreOnMountArgs {
  profileActivationReady: boolean;
  requireExplicitProfileSelection: boolean;
  restoreExistingProfileOnMount: boolean;
  deferProjectPreferredRestore: boolean;
  projectPreferredProfile: RestoreProfileRef | null;
  selectedProfileKey: string;
  selectedProfileKeyRef: MutableRefObject<string>;
  setSelectedProfileKey: Dispatch<SetStateAction<string>>;
  setSelectedSettingsType: Dispatch<SetStateAction<SettingsType>>;
  setLastAppliedProfileKey: Dispatch<SetStateAction<string>>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  setProfileError: Dispatch<SetStateAction<string | null>>;
  setGeneralSettings: Dispatch<SetStateAction<MappingConfiguration['general']>>;
  setAdvancedSettings: Dispatch<SetStateAction<MappingConfiguration['advanced']>>;
}

/**
 * One-shot "resume the previously active profile" effect that runs when the
 * mapping workspace opens over an existing setup (restoreExistingProfileOnMount).
 * Falls back to the Default profile when the preferred one cannot be loaded.
 */
export const useProfileRestoreOnMount = ({
  profileActivationReady,
  requireExplicitProfileSelection,
  restoreExistingProfileOnMount,
  deferProjectPreferredRestore,
  projectPreferredProfile,
  selectedProfileKey,
  selectedProfileKeyRef,
  setSelectedProfileKey,
  setSelectedSettingsType,
  setLastAppliedProfileKey,
  setIsSaving,
  setSaveError,
  setProfileError,
  setGeneralSettings,
  setAdvancedSettings,
}: UseProfileRestoreOnMountArgs) => {
  const restoredProfileRef = useRef(false);
  const restoreAttemptedProfileKeyRef = useRef<string | null>(null);
  const restoreInFlightProfileKeyRef = useRef<string | null>(null);

  useEffect(() => {
    debugMappingProfile('[MappingProfileController] restore gate', {
      profileActivationReady,
      restoreExistingProfileOnMount,
      restoredProfile: restoredProfileRef.current,
      selectedProfileKey,
      deferProjectPreferredRestore,
      projectPreferredProfile,
      requireExplicitProfileSelection,
    });
    if (!profileActivationReady || requireExplicitProfileSelection) {
      return;
    }

    if (deferProjectPreferredRestore && projectPreferredProfile) {
      return;
    }

    if (restoredProfileRef.current) {
      return;
    }

    if (!restoreExistingProfileOnMount) {
      return;
    }

    const activeProfile = getActivePipeexProfile();
    const defaultProfile = {
      name: DEFAULT_PIPEEX_PROFILE_NAME,
      settingsType: SettingsType.User,
    };
    const preferredProfile = activeProfile?.name
      ? activeProfile
      : defaultProfile;
    const scopedPreferredProfile = preferredProfile;
    const restoreAttemptKey = buildProfileKey(
      scopedPreferredProfile.name,
      scopedPreferredProfile.settingsType
    );

    if (
      restoreAttemptedProfileKeyRef.current === restoreAttemptKey ||
      restoreInFlightProfileKeyRef.current === restoreAttemptKey
    ) {
      return;
    }

    restoreInFlightProfileKeyRef.current = restoreAttemptKey;
    let isActive = true;

    const restoreSelectedProfile = async () => {
      const startedAt = nowMs();
      const stallTimer = startProfileStallWarning(
        'profile restore still pending',
        {
          restoreAttemptKey,
          preferredProfile: scopedPreferredProfile,
          projectPreferredProfile,
          activeProfile,
        },
        startedAt
      );
      debugMappingProfile('[MappingProfileController] restoreExistingProfileOnMount start', {
        preferredProfile: scopedPreferredProfile,
        projectPreferredProfile,
        activeProfile,
        defaultProfile,
      });

      setIsSaving(true);
      setSaveError(null);
      setProfileError(null);

      try {
        let dto = null;
        let restoredProfile = scopedPreferredProfile;
        try {
          dto = await getPipeexSettings(scopedPreferredProfile.name, scopedPreferredProfile.settingsType);
        } catch (preferredError) {
          if (
            defaultProfile.name !== scopedPreferredProfile.name ||
            defaultProfile.settingsType !== scopedPreferredProfile.settingsType
          ) {
            console.warn('[MappingProfileController] preferred resume profile failed, trying default fallback', {
              preferredProfile: scopedPreferredProfile,
              defaultProfile,
              error: preferredError instanceof Error ? preferredError.message : String(preferredError),
            });
            try {
              dto = await getPipeexSettings(defaultProfile.name, defaultProfile.settingsType);
              restoredProfile = defaultProfile;
            } catch (defaultError) {
              console.warn('[MappingProfileController] default resume fallback failed', {
                defaultProfile,
                error: defaultError instanceof Error ? defaultError.message : String(defaultError),
              });
              throw defaultError;
            }
          } else {
            throw preferredError;
          }
        }
        if (!isActive) {
          return;
        }

        applyPipeexSettingsToConfigurationStore(dto, {
          restoreWizardProgress: false,
        });
        if (!isActive) {
          return;
        }

        const latest = useConfigurationStore.getState().mappingConfiguration;
        setGeneralSettings(latest.general);
        setAdvancedSettings(latest.advanced);
        const restoredProfileName = restoredProfile.name;
        const restoredSettingsType = restoredProfile.settingsType;
        const nextProfileKey = buildProfileKey(restoredProfileName, restoredSettingsType);
        selectedProfileKeyRef.current = nextProfileKey;
        setSelectedProfileKey(nextProfileKey);
        setSelectedSettingsType(restoredSettingsType);
        setLastAppliedProfileKey(nextProfileKey);
        restoredProfileRef.current = true;
        debugMappingProfile('[MappingProfileController] restoreExistingProfileOnMount applied dto', {
          summary: summarizePcfSettingsDtoForDebug(dto),
          dataFlow: summarizePcfSettingsDtoDataFlowForDebug(dto),
          restoredStoreSnapshot: snapshotCurrentPcfSettingsStoreForDebug().process,
        });
        debugMappingProfile('[MappingProfileController] restoreExistingProfileOnMount complete', {
          restoredProfileKey: nextProfileKey,
          elapsedMs: elapsedMs(startedAt),
        });
      } catch (error) {
        if (!isActive) {
          return;
        }
        warnMappingFlow('profile restore failed', {
          elapsedMs: elapsedMs(startedAt),
          restoreAttemptKey,
          error: errorMessage(error),
        });
        setProfileError(error instanceof Error ? error.message : 'Failed to restore selected profile');
      } finally {
        globalThis.clearTimeout(stallTimer);
        if (restoreInFlightProfileKeyRef.current === restoreAttemptKey) {
          restoreInFlightProfileKeyRef.current = null;
        }

        if (isActive) {
          restoreAttemptedProfileKeyRef.current = restoreAttemptKey;
          clearResumeIntoMappingIntent();
          setIsSaving(false);
        }
      }
    };

    void restoreSelectedProfile();

    return () => {
      isActive = false;
      if (restoreInFlightProfileKeyRef.current === restoreAttemptKey) {
        restoreInFlightProfileKeyRef.current = null;
      }
    };
  }, [
    deferProjectPreferredRestore,
    profileActivationReady,
    projectPreferredProfile,
    requireExplicitProfileSelection,
    restoreExistingProfileOnMount,
    selectedProfileKey,
    setAdvancedSettings,
    setGeneralSettings,
  ]);
};
